import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Broadcast, CalendarX, Buildings, ArrowClockwise, GraduationCap, FileText, Check } from "@phosphor-icons/react";

const roleDivName = (sid) => sid || "Unassigned division";

function SeatChips({ s }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">{s.active} active</span>
      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">{s.grace} grace</span>
      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#B91C1C]">{s.expired} expired</span>
      {s.expiring_soon > 0 && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#FFEDD5] text-[#9A3412]">{s.expiring_soon} expiring soon</span>}
    </div>
  );
}

function RenewalRollup() {
  const [roll, setRoll] = useState(null);
  const [err, setErr] = useState("");
  const [busyOrg, setBusyOrg] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try { const { data } = await api.get("/seats/renewals/rollup"); setRoll(data); }
    catch (e) { setErr(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const drafted = new Set((roll?.draft_quotes || []).map((d) => `${d.org_type}:${d.org_id}`));

  const draftQuote = async (org_type, org_id, org_name, seat_count) => {
    setBusyOrg(`${org_type}:${org_id}`);
    try {
      const { data } = await api.post("/seats/renewals/draft-quote", { org_type, org_id, seat_count });
      toast.success(`Draft quote started for ${org_name}`, { description: `${data.seat_count} seat(s) · ${data.school_year}` });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyOrg(""); }
  };

  if (err) return <div className="card text-sm text-[#FF5100]" data-testid="rollup-error">{err}</div>;
  if (!roll) return null;

  const t = roll.totals || {};
  return (
    <div className="card flex flex-col gap-4" data-testid="renewal-rollup">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Buildings size={22} weight="duotone" color="#84CC16" />
          <h3 className="text-lg font-bold font-display">Renewal rollup — by board & school</h3>
        </div>
        <button className="btn-ghost text-[#000000] flex items-center gap-1 text-sm" onClick={load} data-testid="rollup-refresh"><ArrowClockwise size={15} /> Refresh</button>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="rollup-totals">
        <span className="text-xs uppercase tracking-wider text-[#000000] font-display mr-1">All students</span>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#DCFCE7] text-[#166534]">{t.active || 0} active</span>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#92400E]">{t.grace || 0} in grace</span>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#FEE2E2] text-[#B91C1C]">{t.expired || 0} expired</span>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#FFEDD5] text-[#9A3412]">{t.expiring_soon || 0} expiring soon</span>
      </div>

      {(roll.expiring_blocks || []).length > 0 && (
        <div className="rounded-xl border border-[#F59E0B] bg-[#FFFBEB] p-3" data-testid="expiring-blocks-card">
          <div className="flex items-center gap-2 mb-2">
            <CalendarX size={17} weight="duotone" color="#B45309" />
            <span className="font-bold font-display text-sm text-[#92400E]">Seat blocks expiring soon</span>
          </div>
          <div className="flex flex-col gap-1.5">
            {roll.expiring_blocks.map((b) => (
              <div key={b.block_id} className="flex items-center gap-2 flex-wrap text-sm" data-testid={`expiring-block-${b.block_id}`}>
                {b.demo && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FEF3C7] border border-[#F59E0B] text-[#92400E]">DEMO</span>}
                <b>{b.board_name}</b>
                <span className="text-[#000000]">{b.count.toLocaleString()} seats</span>
                <span className={`font-bold ${b.days_left < 0 ? "text-[#B91C1C]" : "text-[#92400E]"}`}>
                  {b.days_left < 0 ? `expired ${Math.abs(b.days_left)}d ago` : b.days_left === 0 ? "expires today" : `expires in ${b.days_left}d`} ({b.expires_on})
                </span>
                {b.note && <span className="text-xs text-[#94979C]">· {b.note}</span>}
              </div>
            ))}
          </div>
          <div className="text-xs text-[#92400E] mt-2">When a block lapses, its students show up as billable — convert the board with an invoice before then.</div>
        </div>
      )}

      {roll.boards.length === 0 && (
        <div className="text-sm text-[#000000]">No school-track students enrolled yet.</div>
      )}

      {roll.boards.map((b) => (
        <div key={b.board_id || "none"} data-testid={`rollup-board-${b.board_id || "none"}`} className="rounded-xl border border-[#C4C8CE] overflow-hidden">
          <div className="flex items-center justify-between gap-3 bg-[#F4F4F5] px-4 py-2.5 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Buildings size={18} weight="fill" color="#0EA5B7" />
              <span className="font-bold font-display truncate">{b.board_name}</span>
              <span className="text-xs text-[#000000]">· {b.total} student{b.total === 1 ? "" : "s"}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <SeatChips s={b} />
              {b.board_id && (
                drafted.has(`board:${b.board_id}`)
                  ? <span data-testid={`board-drafted-${b.board_id}`} className="text-xs font-bold text-[#166534] flex items-center gap-1"><Check size={14} weight="bold" /> Quote drafted</span>
                  : <button data-testid={`board-draft-${b.board_id}`} onClick={() => draftQuote("board", b.board_id, b.board_name, b.suggested_seats)} disabled={busyOrg === `board:${b.board_id}`} className="btn-primary text-xs flex items-center gap-1" style={{ padding: "4px 12px" }}>
                      <FileText size={14} weight="bold" /> Draft quote ({b.suggested_seats})
                    </button>
              )}
            </div>
          </div>
          <div className="divide-y divide-[#E4E4E7]">
            {b.schools.map((s) => (
              <div key={s.school_id || "none"} data-testid={`rollup-school-${s.school_id || "none"}`} className="flex items-center justify-between gap-3 px-4 py-2.5 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <GraduationCap size={16} weight="duotone" color="#84CC16" />
                  <span className="text-sm font-semibold text-[#000000] truncate">{s.school_name}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <SeatChips s={s} />
                  {s.school_id && (
                    drafted.has(`school:${s.school_id}`)
                      ? <span data-testid={`school-drafted-${s.school_id}`} className="text-xs font-bold text-[#166534] flex items-center gap-1"><Check size={14} weight="bold" /> Drafted</span>
                      : <button data-testid={`school-draft-${s.school_id}`} onClick={() => draftQuote("school", s.school_id, s.school_name, s.suggested_seats)} disabled={busyOrg === `school:${s.school_id}`} className="btn-secondary text-xs flex items-center gap-1" style={{ padding: "4px 10px" }}>
                          <FileText size={13} weight="bold" /> Draft quote ({s.suggested_seats})
                        </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-[11px] text-[#000000]">Draft quotes are saved as a starting point. Full quote composition, PDF export & invoicing arrive with the billing module (Phase 4).</p>
    </div>
  );
}

export default function RenewalsRadarPanel() {
  const [data, setData] = useState(null);
  const [window, setWindow] = useState(60);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (win) => {
    setErr("");
    try {
      const { data } = await api.get("/seats/renewals", { params: { window: win } });
      setData(data);
    } catch (e) { setErr(formatApiError(e)); }
  }, []);

  useEffect(() => { load(window); /* eslint-disable-next-line */ }, []);

  const patch = async (payload) => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", payload);
      await load(window);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleGlobal = (v) => patch({ renewals_radar_enabled: v });
  const toggleDigest = (v) => patch({ renewals_digest_email_enabled: v });
  const [digestMsg, setDigestMsg] = useState("");
  const sendDigestNow = async () => {
    setDigestMsg(""); setSaving(true);
    try {
      const { data } = await api.post("/seats/renewals/digest/send-now");
      setDigestMsg(data.sent ? `Digest sent to ${data.recipients?.length || 0} admin(s) (${data.seat_count} seat(s)).` : `Not sent (${data.reason || "unknown"}).`);
    } catch (e) { setDigestMsg(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const toggleDivision = (sid, v) => {
    const next = { ...(data?.division_overrides || {}) };
    // Only real divisions can be overridden (school_id present).
    next[sid] = v;
    patch({ renewals_radar_division_overrides: next });
  };
  const applyWindow = () => patch({ renewals_radar_window_days: Number(window) });

  if (!data) return <div className="text-sm text-[#000000]">{err || "Loading…"}</div>;

  const enabled = data.enabled;

  return (
    <div className="flex flex-col gap-5" data-testid="renewals-radar-panel">
      <RenewalRollup />
      {/* Controls */}
      <div className="card flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Broadcast size={24} weight="duotone" color="#84CC16" />
          <h2 className="text-xl font-bold font-display">Renewals radar</h2>
        </div>
        <p className="text-sm text-[#000000]">Flags seats nearing their Aug 31 expiry so schools can re-enroll students for the new year.</p>

        <div className="flex items-center justify-between rounded-xl border border-[#C4C8CE] p-3">
          <div>
            <div className="font-semibold text-[#000000]">Enable globally</div>
            <div className="text-xs text-[#000000]">Master switch across all divisions</div>
          </div>
          <Switch data-testid="renewals-global-toggle" checked={enabled} onCheckedChange={toggleGlobal} disabled={saving} />
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1 max-w-[220px]">
            <label className="text-xs uppercase tracking-[0.15em] text-[#000000] font-display">Warning window (days)</label>
            <input data-testid="renewals-window-input" type="number" min="1" max="365" className="input-base mt-1" value={window} onChange={(e) => setWindow(e.target.value)} />
          </div>
          <button data-testid="renewals-window-apply" className="btn-secondary" onClick={applyWindow} disabled={saving}>Apply</button>
          <button className="btn-ghost text-[#000000] flex items-center gap-1" onClick={() => load(window)}><ArrowClockwise size={16} /> Refresh</button>
        </div>

        <div className="rounded-xl border border-[#C4C8CE] p-3 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-[#000000]">Weekly email digest</div>
              <div className="text-xs text-[#000000]">Email Super Admins a weekly list of students up for renewal{data.digest_last_sent ? ` · last sent ${data.digest_last_sent}` : ""}</div>
            </div>
            <Switch data-testid="renewals-digest-toggle" checked={!!data.digest_enabled} onCheckedChange={toggleDigest} disabled={saving} />
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="renewals-digest-sendnow" className="btn-secondary text-xs" style={{padding:"4px 12px"}} onClick={sendDigestNow} disabled={saving}>Send now (test)</button>
            {digestMsg && <span className="text-xs text-[#000000]">{digestMsg}</span>}
          </div>
        </div>
        {err && <div className="text-[#FF5100] text-sm">{err}</div>}
      </div>

      {/* Per-division toggles */}
      {data.divisions.length > 0 && (
        <div className="card flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Buildings size={20} weight="duotone" color="#0EA5B7" />
            <h3 className="text-lg font-bold font-display">By division</h3>
          </div>
          {data.divisions.map((d) => (
            <div key={d.school_id || "none"} data-testid={`renewals-division-${d.school_id || "none"}`} className="flex items-center justify-between rounded-lg border border-[#C4C8CE] p-2.5">
              <div>
                <div className="text-sm font-semibold text-[#000000]">{roleDivName(d.school_id)}</div>
                <div className="text-xs text-[#000000]">{d.count} seat{d.count === 1 ? "" : "s"} nearing expiry</div>
              </div>
              {d.school_id ? (
                <Switch data-testid={`renewals-division-toggle-${d.school_id}`} checked={d.enabled} onCheckedChange={(v) => toggleDivision(d.school_id, v)} disabled={saving || !enabled} />
              ) : (
                <span className="text-xs text-[#000000]">follows global</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Flagged seats */}
      <div className="card flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <CalendarX size={20} weight="duotone" color="#EA580C" />
          <h3 className="text-lg font-bold font-display">Expiring within {data.window_days} days ({data.seats.length})</h3>
        </div>
        {!enabled ? (
          <div className="text-sm text-[#000000]">Renewals radar is turned off. Enable it above to see flagged seats.</div>
        ) : data.seats.length === 0 ? (
          <div className="text-sm text-[#000000]">No seats are expiring within this window.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[#000000] text-xs uppercase tracking-wider">
                <tr><th className="p-2">Student</th><th className="p-2">Division</th><th className="p-2">Attempt</th><th className="p-2">School year</th><th className="p-2">Expires</th><th className="p-2">Days left</th></tr>
              </thead>
              <tbody>
                {data.seats.map((s) => (
                  <tr key={s.id} data-testid={`renewal-seat-${s.id}`} className="border-t border-[#E4E4E7]">
                    <td className="p-2 font-semibold">{s.first_name || "Student"}</td>
                    <td className="p-2 text-[#000000]">{roleDivName(s.school_id)}</td>
                    <td className="p-2 text-[#000000]">#{s.attempt_number}</td>
                    <td className="p-2 text-[#000000]">{s.academic_year}</td>
                    <td className="p-2 text-[#000000]">{s.expires_on}</td>
                    <td className="p-2">
                      <span className={"chip font-bold " + (s.days_left <= 14 ? "" : "")} style={s.days_left <= 14 ? {background:"#FEE2E2",color:"#B91C1C",padding:"1px 8px"} : {background:"#FEF3C7",color:"#92400E",padding:"1px 8px"}}>{s.days_left}d</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
